import * as React from 'react';
import classnames from 'classnames';
import { get, isNil } from 'lodash';

import { Link } from 'libs/router';

import TutorialCarousel from 'components/tutorial-carousel/TutorialCarousel';
import { withModal } from 'components/modal/ModalHOC';
import ActionButton from 'components/buttons/action-button/ActionButton';
import ManagedByTag from 'components/managed-by-tag/ManagedByTag';
import SVGIcon from 'components/svg-icon/SVGIcon';
import CloudinaryImage from 'components/CloudinaryImage';

import CampaignSummaryTeam from '../campaign-summary-team/CampaignSummaryTeam';

import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { isAccepted } from 'utils/subcampaign';
import { getInfluencerTutorialSlides } from 'utils/tutorials';
import { getManagedByBrand, isInfluencerProfileRejected } from 'utils/profile';

const ActionButtonWithModal = withModal(ActionButton, {
  modalContent: TutorialCarousel
});

import type { BrandProfile, Campaign, Subcampaign } from 'types';

applyTranslations(require('./campaign-summary-short.i18n.json'));

type Props = {
  brandProfile: BrandProfile | undefined | null,
  campaign: Campaign,
  subcampaign?: Subcampaign,
  profileType: 'influencer' | 'brand'
};

class CampaignSummaryShort extends React.PureComponent<Props> {
  static defaultProps = {
    profileType: 'brand'
  };

  renderBrandDescription() {
    const { campaign } = this.props;

    const campaignState = campaign.state;
    const stateNameClasses = classnames({
      'campaign-summary-short__state-name': true,
      [`campaign-summary-short__state-name--${campaignState}`]: true
    });

    const campaignText = __t(`campaign_summary_short.state_${campaignState}`, { days: '', date: '' });
    const campaignDates = `${getDisplayDate(campaign.startDate)} - ${getDisplayDate(campaign.endDate)}`;

    return (
      <div className="campaign-summary-short__description">
        <div className="campaign-summary-short__title">
          {campaign.name}
        </div>
        <div className="campaign-summary-short__state">
          <span className="campaign-summary-short__state-icon">
            <SVGIcon
              name={`campaign-${campaignState}`}
              width={22}
              height={22}
            />
          </span>
          <span className={stateNameClasses}>
            {`${campaignText} (${campaignDates})`}
          </span>
        </div>
      </div>
    );
  }

  renderInfluencerDescription() {
    const { campaign, subcampaign, brandProfile } = this.props;
    const endDate = getDisplayDate(campaign.endDate);
    const showTutorialButton = subcampaign && isAccepted(subcampaign) && get(subcampaign, 'offer.budget', 0) > 0;
    const isInfluencerRejected = subcampaign && isInfluencerProfileRejected(subcampaign.creatorProfile);
    const managedByBrand = getManagedByBrand(brandProfile);

    return (
      <div className="campaign-summary-short__description">
        <div className="campaign-summary-short__title">
          {campaign.name}
        </div>
        <div className="campaign-summary-short__footer">
          <div className="campaign-summary-short__footer-info">
            <div className="campaign-summary-short__brand">
              {
                !isNil(brandProfile) && !isNil(brandProfile.name) &&
                  <React.Fragment>
                    <span>by:</span>
                    <Link
                      className="campaign-summary-short__brand-link"
                      to={`/brand/profiles/${brandProfile.id}`}
                    >
                      {brandProfile.name}
                    </Link>
                  </React.Fragment>
              }
            </div>
            <div className="campaign-summary-short__info">
              <div className="campaign-summary-short__managed-info">
                {managedByBrand ?
                  <ManagedByTag
                    className="campaign-summary-short__tag-name"
                    managedByBrand={managedByBrand}
                  />
                  : null}
                <CampaignSummaryTeam
                  className="campaign-summary-short__team"
                  team={campaign.managers}
                />
              </div>
            </div>
          </div>
          {
            showTutorialButton && !isInfluencerRejected &&
            <div className="campaign-summary-short__modal-trigger">
              <ActionButtonWithModal
                modalContentProps={{
                  slides: getInfluencerTutorialSlides(endDate),
                  type: 'influencer'
                }}
                width="minimum"
              >
                <span className="campaign-summary-short__modal-trigger-icon" />
                <span className="campaign-summary-short__modal-trigger-text">
                  {__t('campaign_summary_short.influencer_button_label')}
                </span>
              </ActionButtonWithModal>
            </div>
          }
        </div>
      </div>
    );
  }

  renderCampaignImage() {
    const { campaign } = this.props;

    const campaignMainImage = campaign.mainImage;
    const campaignMainImageComponent = campaignMainImage ? (
      <CloudinaryImage
        image={{ url: campaignMainImage.url }}
        width={120}
        height={120}
        resizable
      />
    ) :
      campaign.name &&
      (
        <div className="campaign-summary-short__image-fallback">
          {campaign.name[0]}
        </div>
      );

    return (
      <div className="campaign-summary-short__image">
        {campaignMainImageComponent || ''}
      </div>
    );
  }

  render() {
    const { campaign, profileType } = this.props;

    if (!campaign || !campaign.id) {
      return null;
    }

    return (
      <section className="campaign-summary-short">
        <div className="campaign-summary-short__content">
          {
            this.renderCampaignImage()
          }
          {
            profileType === 'brand' ?
              this.renderBrandDescription() : this.renderInfluencerDescription()
          }
        </div>
      </section>
    );
  }
}

export default CampaignSummaryShort;
